// Portrait phone layout for Transfers — card + list (Option B)

import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TransferEditFab from './TransferEditFab';
import { faChartPie } from '@fortawesome/free-solid-svg-icons';
import FortnightRangeControl from './FortnightRangeControl';
import { getMobilePillAppearance, formatPillLabel } from './transferMobilePills';
import '../../css/PhoneTransferView.css';

const formatPayDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

const PhoneTransferView = ({
  payData,
  bankAccounts,
  currentPayIndex,
  setCurrentPayIndex,
  fortnightIndex,
  setFortnightIndex,
  formattedRangeFortnight,
  editTransfers,
  setEditTransfers,
  onTogglePaid,
  onCellEditClick,
  onJumpToCurrentFortnight,
}) => {
  const [flashKey, setFlashKey] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const currentPay = payData[currentPayIndex];
  const hasMultiplePays = payData.length > 1;

  const onPaySwipeStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onPaySwipeMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onPaySwipeEnd = () => {
    if (!touchStart || touchEnd === null || payData.length <= 1) {
      return;
    }
    const distance = touchStart - touchEnd;
    if (distance > 50 && currentPayIndex < payData.length - 1) {
      setCurrentPayIndex(currentPayIndex + 1);
    } else if (distance < -50 && currentPayIndex > 0) {
      setCurrentPayIndex(currentPayIndex - 1);
    }
  };

  const handlePillClick = useCallback(
    (payBankAccount, payId) => {
      if (!payBankAccount) return;
      const amount = parseFloat(payBankAccount.transfer_amount);
      if (amount === 0 || payBankAccount.transfer_amount === '0.00') {
        if (!editTransfers) return;
      }

      if (editTransfers) {
        onCellEditClick(payBankAccount, payId);
        return;
      }

      setFlashKey(`${payId}-${payBankAccount.bank_account}`);
      onTogglePaid(payBankAccount);
    },
    [editTransfers, onCellEditClick, onTogglePaid]
  );

  if (!payData.length) {
    return (
      <div className="phone-view">
        <FortnightRangeControl
          formattedRangeFortnight={formattedRangeFortnight}
          fortnightIndex={fortnightIndex}
          setFortnightIndex={setFortnightIndex}
          onJumpToCurrent={onJumpToCurrentFortnight}
          className="phone-fortnight-pill"
        />
        <p className="phone-empty-state">No pays for this fortnight.</p>
        <TransferEditFab editTransfers={editTransfers} setEditTransfers={setEditTransfers} />
      </div>
    );
  }

  return (
    <div
      className="phone-view"
      onTouchStart={onPaySwipeStart}
      onTouchMove={onPaySwipeMove}
      onTouchEnd={onPaySwipeEnd}
    >
      <FortnightRangeControl
        formattedRangeFortnight={formattedRangeFortnight}
        fortnightIndex={fortnightIndex}
        setFortnightIndex={setFortnightIndex}
        onJumpToCurrent={onJumpToCurrentFortnight}
        className="phone-fortnight-pill"
      />

      <div className="phone-pay-card-wrap">
        <div className="phone-pay-card">
          <div className="phone-pay-card-name">
            {currentPay?.nickname || 'Pay'}
            {currentPay?.canUseRemaining && (
              <FontAwesomeIcon
                icon={faChartPie}
                size="xs"
                style={{ marginLeft: 6, opacity: 0.85 }}
                title="Remaining transfers allowed"
              />
            )}
          </div>
          <div className="phone-pay-card-date-row">
            {hasMultiplePays ? (
              <button
                type="button"
                className="phone-pay-card-nav"
                disabled={currentPayIndex === 0}
                onClick={() => setCurrentPayIndex(Math.max(0, currentPayIndex - 1))}
                aria-label="Previous pay"
              >
                ‹
              </button>
            ) : (
              <span className="phone-pay-card-nav--placeholder" aria-hidden="true" />
            )}
            <span className="phone-pay-card-date">
              {formatPayDate(currentPay?.next_pay)}
            </span>
            {hasMultiplePays ? (
              <button
                type="button"
                className="phone-pay-card-nav"
                disabled={currentPayIndex >= payData.length - 1}
                onClick={() =>
                  setCurrentPayIndex(Math.min(payData.length - 1, currentPayIndex + 1))
                }
                aria-label="Next pay"
              >
                ›
              </button>
            ) : (
              <span className="phone-pay-card-nav--placeholder" aria-hidden="true" />
            )}
          </div>
          <div className="phone-pay-card-amount">
            ${parseFloat(currentPay?.amount || 0).toFixed(2)}
          </div>
        </div>
        {hasMultiplePays && (
          <div className="phone-pay-dots" aria-hidden="true">
            {payData.map((_, idx) => (
              <span
                key={idx}
                className={`phone-pay-dot ${idx === currentPayIndex ? 'phone-pay-dot--active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="phone-transfer-list">
        {bankAccounts.map((account) => {
          const payBankAccount = currentPay?.bank_accounts?.find(
            (ba) => ba.bank_account === account.id
          );
          const { className: pillClass } = getMobilePillAppearance(payBankAccount);
          const flashId = currentPay
            ? `${currentPay.id}-${account.id}`
            : null;
          const isZero =
            !payBankAccount ||
            payBankAccount.transfer_amount === '0.00' ||
            parseFloat(payBankAccount?.transfer_amount || 0) === 0;

          return (
            <div key={account.id} className="phone-transfer-row">
              <span className="phone-transfer-account">{account.nickname}</span>
              <button
                type="button"
                className={`phone-amount-pill ${pillClass} ${
                  flashKey === flashId ? 'pill-flash' : ''
                }`}
                onClick={() =>
                  currentPay &&
                  handlePillClick(payBankAccount, currentPay.id)
                }
                disabled={!editTransfers && isZero}
                aria-label={
                  payBankAccount?.transfer_made
                    ? `Mark ${account.nickname} unpaid`
                    : `Mark ${account.nickname} paid`
                }
              >
                {formatPillLabel(payBankAccount)}
              </button>
            </div>
          );
        })}
      </div>

      <div className="phone-color-key">
        <div className="phone-key-item">
          <span className="phone-key-swatch" style={{ backgroundColor: '#d4edda' }} />
          <span>Paid</span>
        </div>
        <div className="phone-key-item">
          <span className="phone-key-swatch" style={{ backgroundColor: '#f8d7da' }} />
          <span>Unpaid</span>
        </div>
        <div className="phone-key-item">
          <span className="phone-key-swatch phone-key-swatch--ring-unallocated" />
          <span>Unallocated</span>
        </div>
        <div className="phone-key-item">
          <span className="phone-key-swatch phone-key-swatch--ring-remaining" />
          <span>Remaining</span>
        </div>
      </div>

      <TransferEditFab editTransfers={editTransfers} setEditTransfers={setEditTransfers} />
    </div>
  );
};

export default PhoneTransferView;
